import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Brain, Loader2, Network, GitBranch,
  Table as TableIcon, Database, Sparkles, Code,
} from 'lucide-react';
import '../../tailwindcss/Tailwind.css';
import exploreApi from './exploreApi';
import DomainNetworkGraph from './components/DomainNetworkGraph';
import QueryContextManager from './components/QueryContextManager';
import SubdomainSQLEditor from './components/SubdomainSQLEditor';

export default function DomainAnalysis() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const sourceId = params.get('source');
  const pathsParam = params.get('paths');

  const userId = sessionStorage.getItem('user_id') || 'user-123';

  const [source, setSource] = useState(null);
  const [paths, setPaths] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showQueryManager, setShowQueryManager] = useState(false);
  const [queryContexts, setQueryContexts] = useState([]);
  const [useQueryContext, setUseQueryContext] = useState(false);
  const [showSQLEditor, setShowSQLEditor] = useState(false);
  const [analysisRuns, setAnalysisRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [selectedSubdomain, setSelectedSubdomain] = useState(null);

  useEffect(() => {
    if (sourceId) loadSource();
    if (pathsParam) {
      try {
        setPaths(JSON.parse(decodeURIComponent(pathsParam)));
      } catch {
        setError('Invalid paths parameter');
      }
    }
  }, [sourceId, pathsParam]);

  useEffect(() => {
    if (sourceId && userId) loadAnalysisRuns();
  }, [sourceId]);

  useEffect(() => {
    if (sourceId && paths.length >= 2 && userId) fetchCachedAnalysis();
  }, [sourceId, paths]);

  const loadSource = async () => {
    try {
      const data = await exploreApi.getSource(sourceId);
      setSource(data);
    } catch {
      setError('Failed to load source information');
    }
  };

  const loadAnalysisRuns = async () => {
    try {
      setLoadingRuns(true);
      const runs = await exploreApi.listDomainAnalysisRuns(sourceId, userId, 20);
      setAnalysisRuns(runs);
    } catch { /* silent */ } finally {
      setLoadingRuns(false);
    }
  };

  const loadAnalysisRun = async (runId) => {
    try {
      setLoading(true);
      const data = await exploreApi.getDomainAnalysisRun(sourceId, runId);
      setAnalysis(data);
      setSelectedRunId(runId);
      setError(null);
    } catch {
      setError('Failed to load analysis run');
    } finally {
      setLoading(false);
    }
  };

  const fetchCachedAnalysis = async () => {
    try {
      const data = await exploreApi.getCachedDomainAnalysis(sourceId, paths, userId);
      if (data) setAnalysis(data);
    } catch { /* silent — cached analysis is optional */ }
  };

  const runDomainAnalysis = async (forceRerun = false) => {
    if (!sourceId || paths.length < 2) return;
    try {
      setLoading(true);
      setError(null);

      if (useQueryContext && queryContexts.length > 0) {
        await exploreApi.saveQueryContexts(sourceId, queryContexts, userId);
      }

      const data = await exploreApi.runDomainAnalysis(sourceId, {
        object_paths: paths,
        include_relationships: true,
        include_sample_data: true,
        user_id: userId,
        force_rerun: forceRerun,
        use_query_context: useQueryContext && queryContexts.length > 0,
        analysis_type: 'user',
        run_name: `Analysis - ${new Date().toLocaleString()}`,
      });

      setAnalysis(data);
      setSelectedRunId(data.analysis_id || null);
      await loadAnalysisRuns();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to analyze domains');
    } finally {
      setLoading(false);
    }
  };

  if (!sourceId || paths.length < 2) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-blue-50/20">
        <div className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10 shadow-sm">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 font-medium">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800 text-sm">
            Please select at least 2 objects from the explore page to perform domain analysis.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-blue-50/20">
      {/* Header */}
      <div className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between w-full flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 font-medium">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Domain Analysis</h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  {source?.name} • {paths.length} objects selected
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Previous runs selector */}
              {analysisRuns.length > 0 && (
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Previous Runs:</label>
                  <select
                    value={selectedRunId || ''}
                    onChange={e => { if (e.target.value) loadAnalysisRun(e.target.value); }}
                    disabled={loading || loadingRuns}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select a previous run...</option>
                    {analysisRuns.map(run => (
                      <option key={run.id} value={run.id}>
                        {run.run_name || `Run ${(run.id || '').substring(0, 8)}`} — {new Date(run.analysis_timestamp).toLocaleString()} ({run.object_count} objects, ${(run.llm_cost || 0).toFixed(4)})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {analysis ? (
                <button
                  onClick={() => runDomainAnalysis(true)}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                >
                  <Sparkles className="h-4 w-4" /> Re-run Analysis
                </button>
              ) : (
                <button
                  onClick={() => runDomainAnalysis(false)}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primaryDark disabled:opacity-40"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {loading ? 'Analyzing...' : 'Analyze Domains'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">{error}</div>
        )}

        {/* Query Context */}
        <div className="bg-white border-2 border-blue-100 rounded-xl p-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Code className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Query Context <span className="text-gray-400 font-normal text-sm">(Optional)</span></h2>
                <p className="text-sm text-gray-500 mt-0.5">Add SQL queries to improve relationship detection and subdomain grouping</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useQueryContext}
                  onChange={e => setUseQueryContext(e.target.checked)}
                  className="w-4 h-4 text-primary border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700">Use Query Context</span>
              </label>
              <button
                onClick={() => setShowQueryManager(true)}
                className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Code className="h-4 w-4" /> Manage Queries ({queryContexts.length})
              </button>
            </div>
          </div>
          {queryContexts.length > 0 && (
            <div className="mt-4 bg-blue-50 rounded-lg p-4 border border-blue-200">
              <p className="text-sm text-gray-600 mb-2">
                <strong>{queryContexts.length}</strong> {queryContexts.length === 1 ? 'query' : 'queries'} added
              </p>
              <div className="flex flex-wrap gap-2">
                {queryContexts.slice(0, 3).map((ctx, i) => (
                  <span key={i} className="px-2 py-0.5 border border-blue-300 rounded-full text-xs text-blue-700 bg-white">
                    {ctx.purpose || `Query ${i + 1}`}
                  </span>
                ))}
                {queryContexts.length > 3 && (
                  <span className="px-2 py-0.5 border border-blue-300 rounded-full text-xs text-blue-700 bg-white">
                    +{queryContexts.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Selected Objects */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">Selected Objects</h2>
            <p className="text-sm text-gray-500 mt-0.5">Objects included in domain analysis</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {paths.map((path, i) => (
                <div key={i} className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <TableIcon className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-sm font-medium text-gray-900 truncate">{(path || '').split('.').pop()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Analysis Metadata */}
        {analysis && (
          <div className="bg-white border-2 border-green-100 rounded-xl overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-green-50 to-white border-b border-green-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Sparkles className="h-5 w-5 text-green-600" />
                </div>
                <h2 className="text-base font-semibold text-gray-900">Analysis Information</h2>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 font-medium">Run Name</p>
                  <p className="text-gray-900 font-semibold">{analysis.run_name || 'Unnamed'}</p>
                </div>
                <div>
                  <p className="text-gray-500 font-medium">Type</p>
                  <p className="text-gray-900 font-semibold capitalize">{analysis.analysis_type || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500 font-medium">Objects Analyzed</p>
                  <p className="text-gray-900 font-semibold">{analysis.object_count || paths.length}</p>
                </div>
                <div>
                  <p className="text-gray-500 font-medium">Total Cost</p>
                  <p className="text-gray-900 font-semibold">${(analysis.llm_usage?.cost_estimate ?? analysis.total_cost ?? 0).toFixed(4)}</p>
                </div>
                {analysis.total_tokens && (
                  <div>
                    <p className="text-gray-500 font-medium">Tokens Used</p>
                    <p className="text-gray-900 font-semibold">{analysis.total_tokens.toLocaleString()}</p>
                  </div>
                )}
                {analysis.llm_model && (
                  <div>
                    <p className="text-gray-500 font-medium">Model</p>
                    <p className="text-gray-900 font-semibold">{analysis.llm_model}</p>
                  </div>
                )}
                {analysis.analysis_id && (
                  <div className="col-span-2">
                    <p className="text-gray-500 font-medium">Analysis ID</p>
                    <p className="text-gray-900 font-mono text-xs">{analysis.analysis_id}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-12 text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Analyzing Object Relationships...</h3>
            <p className="text-gray-600">AI is identifying domains, subdomains, and relationships</p>
          </div>
        )}

        {/* Results */}
        {analysis && !loading && (
          <div className="space-y-6">
            {/* Network Graph */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <Network className="h-5 w-5 text-primary" />
                  <h2 className="text-base font-semibold text-gray-900">Domain Network Graph</h2>
                </div>
              </div>
              <div className="p-4">
                <DomainNetworkGraph domains={analysis.domains} relationships={analysis.relationships} />
              </div>
            </div>

            {/* Summary */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <Brain className="h-5 w-5 text-primary" />
                  <h2 className="text-base font-semibold text-gray-900">Analysis Summary</h2>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-700 leading-relaxed">{analysis.summary}</p>
                {analysis.llm_usage && (
                  <div className="mt-4 pt-4 border-t flex flex-wrap items-center gap-4 text-sm text-gray-600">
                    {analysis.cached && (
                      <>
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">Cached Result</span>
                        {analysis.analysis_timestamp && (
                          <span>Analyzed: {new Date(analysis.analysis_timestamp).toLocaleString()}</span>
                        )}
                        <span>•</span>
                      </>
                    )}
                    <span>Model: {analysis.llm_usage.model}</span>
                    <span>•</span>
                    <span>Tokens: {analysis.llm_usage.total_tokens?.toLocaleString() || 'N/A'}</span>
                    <span>•</span>
                    <span>Cost: ${(analysis.llm_usage.cost_estimate || 0).toFixed(4)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Domains */}
            <div className="space-y-4">
              {analysis.domains.map((domain, domainIdx) => (
                <div key={domainIdx} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                  <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <Database className="h-5 w-5 text-primary" />
                          <h2 className="text-base font-semibold text-gray-900">{domain.name}</h2>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{domain.description}</p>
                      </div>
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                        {Math.round(domain.confidence * 100)}% confidence
                      </span>
                    </div>
                  </div>
                  <div className="p-6 space-y-4">
                    {domain.subdomains.map((subdomain, subIdx) => (
                      <div key={subIdx} className="border border-blue-100 rounded-lg p-4 bg-blue-50/50">
                        <div className="flex items-start gap-3 mb-3">
                          <GitBranch className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <h4 className="font-semibold text-gray-900">{subdomain.name}</h4>
                              <button
                                onClick={() => {
                                  setSelectedSubdomain({
                                    name: subdomain.name,
                                    tables: subdomain.objects.map(o => o.path),
                                    description: subdomain.description,
                                    business_purpose: subdomain.business_purpose,
                                    objects: subdomain.objects,
                                    relationships: [],
                                  });
                                  setShowSQLEditor(true);
                                }}
                                className="flex items-center gap-1.5 px-2.5 py-1 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50"
                              >
                                <Code className="h-3 w-3" /> Generate SQL
                              </button>
                            </div>
                            <p className="text-sm text-gray-700 mb-2">{subdomain.description}</p>
                            <span className="text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded inline-block">
                              {subdomain.business_purpose}
                            </span>
                          </div>
                        </div>

                        <div className="mt-3 space-y-2">
                          {subdomain.objects.map((obj, objIdx) => (
                            <div key={objIdx} className="bg-white border border-gray-200 rounded p-3">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <TableIcon className="h-4 w-4 text-gray-600" />
                                  <span className="font-medium text-sm text-gray-900">{obj.name}</span>
                                  <span className="px-1.5 py-0.5 border border-gray-300 rounded text-xs text-gray-600">{obj.type}</span>
                                </div>
                                {obj.row_count && (
                                  <span className="text-xs text-gray-500">{obj.row_count.toLocaleString()} rows</span>
                                )}
                              </div>
                              {obj.key_columns?.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {obj.key_columns.map((col, colIdx) => (
                                    <span key={colIdx} className="px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">{col}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Relationships */}
            {analysis.relationships?.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <Network className="h-5 w-5 text-primary" />
                    <h2 className="text-base font-semibold text-gray-900">Identified Relationships</h2>
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">Connections between objects</p>
                </div>
                <div className="p-6 space-y-3">
                  {analysis.relationships.map((rel, i) => (
                    <div key={i} className="flex items-center gap-4 p-3 border border-gray-200 rounded-lg bg-gray-50">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm text-gray-900">{rel.from_object.split('.').pop()}</span>
                          <ArrowRight className="h-4 w-4 text-gray-400" />
                          <span className="font-medium text-sm text-gray-900">{rel.to_object.split('.').pop()}</span>
                          <span className="px-1.5 py-0.5 border border-gray-300 rounded text-xs text-gray-600">{rel.type}</span>
                        </div>
                        <p className="text-xs text-gray-600">{rel.description}</p>
                      </div>
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                        {Math.round(rel.confidence * 100)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showQueryManager && (
        <QueryContextManager
          onClose={() => setShowQueryManager(false)}
          onSave={(contexts) => {
            setQueryContexts(contexts);
            setShowQueryManager(false);
            setUseQueryContext(true);
          }}
          existingContexts={queryContexts}
        />
      )}

      {showSQLEditor && sourceId && (
        <SubdomainSQLEditor
          isOpen={showSQLEditor}
          onClose={() => { setShowSQLEditor(false); setSelectedSubdomain(null); }}
          subdomain={selectedSubdomain}
          sourceId={sourceId}
        />
      )}
    </div>
  );
}
